import * as pc from "playcanvas";
import { getWeapon } from "@hupper/shared/weapons";
import type { GameClient, RemotePlayerState } from "../net/GameClient";
import { resumeAudioContext, unlockAudioOnGesture } from "./audioContext";

/**
 * UISounds — economy / inventory feedback for the local player (2D):
 *   • buy            — any menu purchase (detected as the player's money
 *                      dropping; covers weapons, armor, nades, ammo)
 *   • pickup weapon  — a gun acquired for free (picked up off the ground)
 *   • pickup grenade — a grenade acquired for free
 *
 * Driven off the authoritative self-snapshot diff (money + ownedWeapons),
 * so it's decoupled from the buy menu / pickup UI and can't double-fire.
 * Round-start loadout resets (a wholesale ownedWeapons swap) are ignored.
 */

const BUY_URL = "/audio/buy.mp3";
const PICKUP_WEAPON_URL = "/audio/pickup-weapon.mp3";
const PICKUP_GRENADE_URL = "/audio/pickup-grenade.mp3";
const ALL_URLS = [BUY_URL, PICKUP_WEAPON_URL, PICKUP_GRENADE_URL];
const VOLUME = 0.55;

const slotName = (url: string): string => url.replace(/[^a-z0-9]/gi, "_");

export class UISounds {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly emitter: pc.Entity;
  private readonly assets = new Map<string, pc.Asset>();
  private ready = false;
  private readonly unsubs: Array<() => void> = [];
  private lastMoney: number | null = null;
  private lastOwned: string[] = [];
  private initialized = false;

  constructor(app: pc.AppBase, client: GameClient) {
    this.app = app;
    this.client = client;

    this.emitter = new pc.Entity("ui-snd");
    this.app.root.addChild(this.emitter);

    this.loadAssets();
    unlockAudioOnGesture(this.app);

    this.unsubs.push(
      this.client.onPlayerChange((self, sid) => {
        if (sid === this.client.sessionId) this.onSelfUpdate(self);
      }),
    );
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.emitter.destroy();
  }

  private onSelfUpdate(self: RemotePlayerState): void {
    const money = self.money ?? 0;
    const owned = (self.ownedWeapons ?? []).slice().sort();
    if (!this.initialized) {
      this.initialized = true;
      this.lastMoney = money;
      this.lastOwned = owned;
      return;
    }

    const moneyDown = this.lastMoney !== null && money < this.lastMoney;
    const added = owned.filter((w) => !this.lastOwned.includes(w));
    const removed = this.lastOwned.filter((w) => !owned.includes(w));

    if (moneyDown) {
      // Any purchase (weapon / armor / grenade / ammo) — money was spent.
      this.play(BUY_URL);
    } else if (added.length === 1 && removed.length <= 1) {
      // Free single acquisition = a ground pickup (not a round-reset swap).
      this.play(this.isGrenade(added[0]) ? PICKUP_GRENADE_URL : PICKUP_WEAPON_URL);
    }

    this.lastMoney = money;
    this.lastOwned = owned;
  }

  private isGrenade(weaponId: string): boolean {
    try {
      return getWeapon(weaponId).class === "grenade";
    } catch {
      return false;
    }
  }

  private play(url: string): void {
    if (!this.ready) return;
    resumeAudioContext(this.app);
    this.emitter.sound?.play(slotName(url));
  }

  private loadAssets(): void {
    let remaining = ALL_URLS.length;
    for (const url of ALL_URLS) {
      this.app.assets.loadFromUrl(url, "audio", (err, asset) => {
        if (err || !asset) console.warn("[UISounds] failed to load:", url, err);
        else this.assets.set(url, asset);
        if (--remaining === 0) this.onAssetsReady();
      });
    }
  }

  private onAssetsReady(): void {
    this.emitter.addComponent("sound");
    const s = this.emitter.sound;
    if (s) {
      s.positional = false; // 2D — UI feedback
      for (const url of ALL_URLS) {
        const asset = this.assets.get(url);
        if (asset) {
          s.addSlot(slotName(url), { asset: asset.id, volume: VOLUME, loop: false, autoPlay: false, overlap: true });
        }
      }
    }
    this.ready = true;
  }
}
